import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { adminErrorsApi } from "../../lib/authApi";
import { EmptyLog } from "../../components/illustrations/EmptyIllustrations";
import { EmptyState } from "../../components/ui/empty-state";
import { GridSkeletonRows } from "../../components/ui/grid-skeleton";

const LEVEL_COLOR = {
  ERROR: { bg: "#FEF2F2", color: "#991B1B", border: "#FECACA" },
  WARNING: { bg: "#FFFBEB", color: "#92400E", border: "#FDE68A" },
  CRITICAL: { bg: "#FEE2E2", color: "#7F1D1D", border: "#FCA5A5" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(d);
  } catch {
    return iso;
  }
}

export default function AdminErrors() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [expanded, setExpanded] = useState({});
  const [filters, setFilters] = useState({
    endpoint: "",
    level: "",
    date_from: "",
    date_to: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminErrorsApi.list({ ...filters, skip, limit });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento error log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  const applyFilters = () => {
    setSkip(0);
    load();
  };

  const resetFilters = () => {
    setFilters({ endpoint: "", level: "", date_from: "", date_to: "" });
    setSkip(0);
    setTimeout(load, 0);
  };

  return (
    <div className="p-6 space-y-4 max-w-[1400px] mx-auto" data-testid="admin-errors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[22px] font-semibold" style={{ color: "var(--text-primary)" }}>
            Log errori
          </h1>
          <p className="text-[12px] mt-0.5" style={{ color: "var(--text-muted)" }}>
            Eccezioni non gestite catturate dal server
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded text-[13px]"
          style={{
            background: "var(--bg-surface)",
            border: "1px solid var(--border-default)",
            color: "var(--text-primary)",
          }}
          data-testid="errors-refresh-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      {/* Filters */}
      <div
        className="rounded p-3 grid grid-cols-1 md:grid-cols-4 gap-2"
        style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <input
          type="text"
          placeholder="Endpoint…"
          value={filters.endpoint}
          onChange={(e) => setFilters({ ...filters, endpoint: e.target.value })}
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
          data-testid="errors-filter-endpoint"
        />
        <select
          value={filters.level}
          onChange={(e) => setFilters({ ...filters, level: e.target.value })}
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
          data-testid="errors-filter-level"
        >
          <option value="">Tutti i livelli</option>
          <option value="ERROR">ERROR</option>
          <option value="WARNING">WARNING</option>
          <option value="CRITICAL">CRITICAL</option>
        </select>
        <input
          type="date"
          value={filters.date_from?.slice(0, 10) || ""}
          onChange={(e) =>
            setFilters({
              ...filters,
              date_from: e.target.value ? `${e.target.value}T00:00:00` : "",
            })
          }
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
        />
        <input
          type="date"
          value={filters.date_to?.slice(0, 10) || ""}
          onChange={(e) =>
            setFilters({
              ...filters,
              date_to: e.target.value ? `${e.target.value}T23:59:59` : "",
            })
          }
          className="h-8 px-2 rounded text-[12px]"
          style={{ border: "1px solid var(--border-default)" }}
        />
        <div className="md:col-span-4 flex gap-2 justify-end">
          <button
            type="button"
            onClick={resetFilters}
            className="h-8 px-3 text-[12px] rounded"
            style={{
              background: "transparent",
              color: "var(--text-secondary)",
            }}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={applyFilters}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded text-[12px] font-medium"
            style={{
              background: "var(--accent-primary)",
              color: "var(--text-on-accent)",
            }}
            data-testid="errors-apply-btn"
          >
            <Search className="h-3 w-3" />
            Filtra
          </button>
        </div>
      </div>

      <div
        className="rounded overflow-hidden"
        style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border-subtle)",
        }}
      >
        <div
          className="grid text-[11px] font-semibold uppercase tracking-wide"
          style={{
            gridTemplateColumns: "180px 100px 220px 1fr 32px",
            background: "var(--bg-subtle)",
            color: "var(--text-muted)",
          }}
        >
          <div className="px-3 py-2.5">Data e ora</div>
          <div className="px-3 py-2.5">Livello</div>
          <div className="px-3 py-2.5">Endpoint</div>
          <div className="px-3 py-2.5">Messaggio</div>
          <div className="px-3 py-2.5"></div>
        </div>
        {loading ? (
          <GridSkeletonRows rows={6} cols={5} />
        ) : items.length === 0 ? (
          <EmptyState
            illustration={EmptyLog}
            title="Nessun errore registrato"
            subtitle="Le eccezioni non gestite compariranno qui."
          />
        ) : (
          items.map((it) => {
            const id = it._id || `${it.ts}-${it.message?.slice(0, 12)}`;
            const isOpen = !!expanded[id];
            const lv = LEVEL_COLOR[it.level] || LEVEL_COLOR.ERROR;
            return (
              <div key={id}>
                <div
                  className="grid items-center text-[12px] cursor-pointer"
                  style={{
                    gridTemplateColumns: "180px 100px 220px 1fr 32px",
                    borderTop: "1px solid var(--border-subtle)",
                  }}
                  onClick={() => setExpanded((s) => ({ ...s, [id]: !s[id] }))}
                  data-testid={`error-row-${id}`}
                >
                  <div
                    className="px-3 py-2.5 font-mono text-[11px]"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {fmtDate(it.ts)}
                  </div>
                  <div className="px-3 py-2.5">
                    <span
                      className="inline-flex items-center text-[10px] font-semibold uppercase px-2 py-0.5 rounded-sm"
                      style={{
                        background: lv.bg,
                        color: lv.color,
                        border: `1px solid ${lv.border}`,
                      }}
                    >
                      {it.level || "ERROR"}
                    </span>
                  </div>
                  <div
                    className="px-3 py-2.5 truncate font-mono text-[11px]"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {it.method ? `${it.method} ` : ""}
                    {it.endpoint || "—"}
                  </div>
                  <div
                    className="px-3 py-2.5 truncate"
                    style={{ color: "var(--text-primary)" }}
                    title={it.message}
                  >
                    {it.message}
                  </div>
                  <div className="px-3 py-2.5" style={{ color: "var(--text-muted)" }}>
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div
                    className="px-3 py-3"
                    style={{
                      background: "var(--bg-subtle)",
                      borderTop: "1px solid var(--border-subtle)",
                    }}
                    data-testid={`error-row-${id}-detail`}
                  >
                    <div
                      className="text-[11px] font-mono whitespace-pre-wrap rounded p-3"
                      style={{
                        background: "#1E293B",
                        color: "#E2E8F0",
                        maxHeight: "320px",
                        overflow: "auto",
                      }}
                    >
                      {it.stack_trace || "(stack trace non disponibile)"}
                    </div>
                    <div
                      className="text-[11px] mt-2"
                      style={{ color: "var(--text-muted)" }}
                    >
                      User ID: <span className="font-mono">{it.user_id || "—"}</span>{" "}
                      · Request ID:{" "}
                      <span className="font-mono">{it.request_id || "—"}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div
        className="flex items-center justify-between text-[12px]"
        style={{ color: "var(--text-muted)" }}
      >
        <div>
          {total > 0
            ? `Voci ${skip + 1}–${Math.min(skip + limit, total)} di ${total}`
            : "0 voci"}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0 || loading}
            className="h-8 px-3 rounded text-[12px] disabled:opacity-40"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-default)",
              color: "var(--text-primary)",
            }}
          >
            Precedente
          </button>
          <button
            type="button"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total || loading}
            className="h-8 px-3 rounded text-[12px] disabled:opacity-40"
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-default)",
              color: "var(--text-primary)",
            }}
          >
            Successiva
          </button>
        </div>
      </div>
    </div>
  );
}
